import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useProperties } from '@/hooks/use-property-queries';
import { usePermissions } from '@/hooks/use-permissions';
import { PageLoader } from '@/components/page-loader';
import { PERMISSIONS } from '@property/shared';

export const Route = createFileRoute('/dashboard/')({
  // Escape hatch for the empty-owner → add-property redirect: leave-confirm from the
  // wizard passes this so `/dashboard` can fall through to land-monitoring instead of
  // bouncing the user straight back into the form.
  validateSearch: (search: Record<string, unknown>): { skipAddProperty?: boolean } => ({
    // URL bar / page.goto hand us the string "true"; typed navigate() hands a boolean.
    skipAddProperty:
      search.skipAddProperty === true || search.skipAddProperty === 'true' ? true : undefined,
  }),
  component: DashboardOverview,
});

function DashboardOverview() {
  const { data: properties, isLoading, isFetching, isStale, isError } = useProperties();
  const { can, role, isLoading: permissionsLoading } = usePermissions();
  const { skipAddProperty } = Route.useSearch();
  const navigate = useNavigate();
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current || permissionsLoading || isLoading || isFetching || isStale) return;

    redirected.current = true;

    if (!isError && role === 'user' && properties?.length === 0 && !skipAddProperty) {
      navigate({ to: '/dashboard/land-monitoring/add-property' });
    } else if (can(PERMISSIONS.TASKS_READ)) {
      navigate({ to: '/dashboard/tasks' });
    } else if (can(PERMISSIONS.INSPECTIONS_REVIEW)) {
      navigate({ to: '/dashboard/reviews' });
    } else if (can(PERMISSIONS.PROPERTIES_READ)) {
      navigate({ to: '/dashboard/land-monitoring' });
    } else if (can(PERMISSIONS.ROLES_ASSIGN)) {
      navigate({ to: '/dashboard/users' });
    }
  }, [
    permissionsLoading,
    isLoading,
    isFetching,
    isStale,
    isError,
    role,
    properties,
    skipAddProperty,
    navigate,
    can,
  ]);

  return <PageLoader />;
}
